import { registerPlugin, Capacitor } from "@capacitor/core";
import type { LivingSyncsTokenStore } from "../platform";
import { captureMessage, captureException } from "../sentry";

/**
 * JS-side wrapper for the local KeychainTokenStorePlugin (US-015 AC10) —
 * mobile/ios/App/App/KeychainTokenStorePlugin.swift. Implements the SAME
 * LivingSyncsTokenStore contract the Electron desktop shell's safeStorage bridge
 * implements, so cognito.ts's setKeyValueStorage() call works unchanged for both
 * shells. `available` is false (and every method is unreachable) on web/Electron,
 * where the native plugin was never registered.
 */
interface KeychainTokenStorePlugin {
  getItem(options: { key: string }): Promise<{ value: string | null }>;
  setItem(options: { key: string; value: string }): Promise<void>;
  removeItem(options: { key: string }): Promise<void>;
  clear(): Promise<void>;
}

const native = registerPlugin<KeychainTokenStorePlugin>("KeychainTokenStore");

const available = Capacitor.isPluginAvailable("KeychainTokenStore");

/**
 * Defense-in-depth (review finding): if the plugin is EXPECTED here — we're
 * definitely inside the real native shell — but missing, Amplify silently
 * falls back to its weaker default store with no other signal anywhere.
 * Never includes the key or value; this only ever fires before any token
 * exists.
 *
 * MUST be called explicitly AFTER initSentry() has run — main.tsx does,
 * right after its own initSentry() call — never as a module-scope side
 * effect. A cycle-2 review (independently, twice) caught this exact bug in
 * an earlier version: this module is imported transitively via
 * capacitorBridge.ts, which is deliberately main.tsx's FIRST import (see
 * its own header comment) — i.e. before initSentry() has run. Sentry's own
 * Scope.captureMessage() silently no-ops with no client bound yet, so a
 * module-scope call here reproduced the exact "zero signal anywhere"
 * failure this exists to catch.
 */
export function reportKeychainStoreUnavailability(): void {
  if (Capacitor.isNativePlatform() && !available) {
    captureMessage(
      "KeychainTokenStore plugin unavailable on native platform — falling back to default token storage",
      "error",
    );
  }
}

/**
 * I1 (review finding, empirically confirmed real — see the Swift plugin's
 * own comments): a Simulator build with CODE_SIGNING_ALLOWED=NO has no
 * signature identity at all, so SecItemAdd/Update genuinely rejects with
 * errSecMissingEntitlement. With no error handling here, that rejection
 * propagated silently past this module into Amplify, which fell back to its
 * own default store with zero signal anywhere — the exact "silent
 * downgrade" this module already guards against for total plugin
 * unavailability, now proven to also happen per-operation.
 *
 * Reports (non-PHI: which operation, never the key or value) AND re-throws.
 * Re-throw, not swallow: we've now observed Amplify degrades gracefully on
 * a rejection (this session's own diagnostic), which is the one thing both
 * cycle-2 reviews said not to guess at without evidence. Swallowing instead
 * risks Amplify believing a write succeeded when it didn't.
 */
function reportAndRethrow(operation: string, error: unknown): never {
  captureException(error instanceof Error ? error : new Error(String(error)), {
    operation,
    source: "keychainTokenStore",
  });
  throw error;
}

export const keychainTokenStore: LivingSyncsTokenStore = {
  available,
  async getItem(key) {
    try {
      const { value } = await native.getItem({ key });
      return value;
    } catch (err) {
      reportAndRethrow("getItem", err);
    }
  },
  async setItem(key, value) {
    try {
      await native.setItem({ key, value });
    } catch (err) {
      reportAndRethrow("setItem", err);
    }
  },
  async removeItem(key) {
    try {
      await native.removeItem({ key });
    } catch (err) {
      reportAndRethrow("removeItem", err);
    }
  },
  async clear() {
    try {
      await native.clear();
    } catch (err) {
      reportAndRethrow("clear", err);
    }
  },
};
